import { useEffect, useMemo, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { PRODUCT_INDEX_ROUTE, getStepByRoute } from '../demo/steps';
import TopNav from './TopNav';
import StepBar from './StepBar';
import ProductSidebar from './ProductSidebar';
import './shell.css';

/* Moves focus to the page's <main id="main" tabIndex={-1}> without touching the router's URL. */
function skipToContent(event) {
  const main = document.getElementById('main');
  if (!main) return;
  event.preventDefault();
  main.focus();
  main.scrollIntoView?.();
}

/*
 * The frame around every page. Home and Not found get only the top nav; step
 * routes add the step bar. Every product step after the intro, and the hidden
 * product index (not a step, so no step bar), add the product sidebar. The intro (/product) is a full-bleed scene:
 * no sidebar, no page background.
 */
export default function StepLayout() {
  const location = useLocation();
  const { pathname } = location;

  // The last location this app showed. A page reads it while it renders (the
  // effect below has not yet run for the new location), so it is the previous
  // in-app location, or null on a first load. The intro uses it to tell a
  // browser Back / Forward from a direct load.
  const lastLocationRef = useRef(null);
  useEffect(() => {
    lastLocationRef.current = location;
  }, [location]);
  const outletContext = useMemo(() => ({ lastLocationRef }), []);
  const step = getStepByRoute(pathname);
  const isIndex = pathname.replace(/\/+$/, '') === PRODUCT_INDEX_ROUTE;
  const isIntro = step?.route === '/product';
  const withSidebar = isIndex || Boolean(step && step.section === 'product' && !isIntro);
  const sidebarRoute = isIndex ? PRODUCT_INDEX_ROUTE : step?.route;

  const classes = ['layout'];
  if (step) classes.push('layout--step');
  if (withSidebar) classes.push('layout--sidebar');
  if (isIntro) classes.push('layout--bleed');

  return (
    <div className={classes.join(' ')}>
      <a href="#main" className="skip-link" onClick={skipToContent} data-trapezium-id="shell.skip-to-content">
        Skip to content
      </a>
      <TopNav />
      <div className="layout__body">
        {withSidebar && <ProductSidebar currentRoute={sidebarRoute} />}
        <div className="layout__page">
          <Outlet context={outletContext} />
        </div>
      </div>
      {step && <StepBar step={step} />}
    </div>
  );
}
